import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormLabel,
  Tooltip,
  Button,
  Autocomplete,
} from "@mui/material";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import {
  checkData,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { Guarantee } from "../../../constants/GuaranteeField";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import {
  golfTooltipHeaders,
  originsTooltip,
} from "../../../constants/TooltipInformation";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import { removeSampleTestArray } from "../../actions/cleanup";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const GolfPlayerRate = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [playerType, setPlayerType] = useState<string | null>(null);
  const [rateType, setRateType] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [GolfRateAllocations, setGolfRateAllocations] = useState<any>([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [allocationBlockList, setAllocationBlockList] = useState<string[]>([]);
  const [playerList, setPlayerList] = useState<string[]>([]);
  const [rateList, setRateList] = useState<string[]>([]);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const buildRateAllocationRecords = () =>
    rateType.map((type) => ({
      "Player Type": playerType,
      "Rate Type": type,
    }));

  const updateGolfRateAllocations = () => {
    if (!playerType || !rateType || rateType.length === 0) {
      setAlertMessage("Player Type and Rate Type are mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    const records = buildRateAllocationRecords();
    setGolfRateAllocations((prev: any) => {
      const others = (prev ?? []).filter((item: any) => item.id !== selectedId);
      return updateData([...others, ...records]);
    });
    editGolfRateAllocations(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${GolfPropertyEnum.playerRateAllocation} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addGolfRateAllocations = () => {
    if (!playerType || !rateType || rateType.length === 0) {
      setAlertMessage("Player Type and Rate Type are mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    const records = buildRateAllocationRecords();
    setGolfRateAllocations((prev: any) =>
      updateData([...(prev ?? []), ...records]),
    );
    setLastUpdatedRecord(records[0]);
    editGolfRateAllocations(null);
    setEditClicked(false);
  };

  const editGolfRateAllocations = (list: any) => {
    setSelectedId(list?.id ?? null);
    setEditClicked(list != null);
    setPlayerType(list?.["Player Type"] ?? null);
    setRateType(
      list?.["Rate Type"]
        ? String(list["Rate Type"]).includes(", ")
          ? String(list["Rate Type"]).split(", ")
          : [String(list["Rate Type"])]
        : [],
    );
  };

  const deleteGolfRateAllocations = (id: number) => {
    setGolfRateAllocations(deleteItem(GolfRateAllocations ?? [], id));
  };
  useEffect(() => {
    if (fieldValues) {
      setGolfRateAllocations(
        fieldValues[GolfPropertyEnum.playerRateAllocation],
      );
      setAllocationBlockList(
        fillAutocompleteValues(
          fieldValues[GolfPropertyEnum.allocationBlocks],
          DNDFieldsEnum.ALLOCATIONBLOCKCODE,
          "",
        ),
      );
      setPlayerList(
        fillAutocompleteValues(
          fieldValues[GolfPropertyEnum.playerTypes],
          DNDFieldsEnum.PLAYERTYPE,
          "",
        ),
      );
      setRateList(
        fillAutocompleteValues(
          fieldValues[GolfPropertyEnum.rateTypes],
          DNDFieldsEnum.RATETYPE,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    data(GolfRateAllocations);
    editClicked &&
      setSelectedId(
        checkData(GolfRateAllocations, selectedId) ? selectedId : null,
      );
  }, [GolfRateAllocations]);

  return (
    <Stack>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Typography variant="h6" component="div" sx={textStyle}>
            {GolfPropertyEnum.playerRateAllocation}
            <Tooltip title={tooltipComponent("")}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px" justifyContent="space-between">
            {editClicked && (
              <ClearButton
                click={() => {
                  editGolfRateAllocations(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton
                click={() => {
                  editGolfRateAllocations(lastUpdatedRecord);
                }}
              />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="5px">
          <FormLabel component="legend">
            Player Type
            <Tooltip title={tooltipComponent(golfTooltipHeaders.minMinutes)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <Autocomplete
            id="allocationBlock"
            disablePortal
            options={removeSampleTestArray(playerList)}
            sx={{ width: "100%" }}
            value={playerType?.toString() ?? ""}
            disabled={!playerList}
            onChange={(e: any) => setPlayerType(e.target.innerText)}
            renderInput={(params) => (
              <TextField {...params} label="Player Type" />
            )}
          />
        </Stack>
        <Stack direction="column" width="100%" gap="5px">
          <FormLabel component="legend">
            Rate Type
            <Tooltip title={tooltipComponent(golfTooltipHeaders.minMinutes)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <Autocomplete
            multiple
            id="rateType"
            disablePortal
            options={removeSampleTestArray(rateList)}
            sx={{ width: "100%" }}
            value={rateType}
            disabled={!rateList}
            onChange={(event, newValue) => setRateType(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Rate Type" />
            )}
          />
        </Stack>
        <Stack direction="row" width="100%" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() =>
              selectedId
                ? updateGolfRateAllocations()
                : addGolfRateAllocations()
            }
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
            Rate Allocation
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Rate Allocation Available</Typography>
          <Stack direction="column" gap="10px">
            {GolfRateAllocations ? (
              <DraggableItemList
                itemData={GolfRateAllocations}
                onOrderUpdate={(order) => setGolfRateAllocations(order)}
                onEdit={(info) => editGolfRateAllocations(info)}
                onDelete={(id) => deleteGolfRateAllocations(id)}
                code={DNDFieldsEnum.PLAYERTYPE}
                name={DNDFieldsEnum.RATETYPE}
              />
            ) : (
              <Typography variant="body2">
                No Rate Block Information Available
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};
export default GolfPlayerRate;
